import { ChevronRight, FolderClosed } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { DocumentEmptyState } from '@/components/documents/DocumentEmptyState'
import { DocumentRow } from '@/components/documents/DocumentRow'
import { DocumentSearchCombobox } from '@/components/documents/DocumentSearchCombobox'
import { DocumentTableHeader } from '@/components/documents/DocumentTableHeader'
import { DocumentTableSkeleton } from '@/components/documents/DocumentTableSkeleton'
import { DOCUMENT_GRID_COLS, DOCUMENT_GRID_COLS_CLASS } from '@/components/documents/document-grid'
import { ExtractionConfigModal } from '@/components/documents/ExtractionConfigModal'
import { ExtractionStatusCard } from '@/components/documents/ExtractionStatusCard'
import { FolderRow } from '@/components/documents/FolderRow'
import { SchemaSetupBanner, SchemaSetupPrompt } from '@/components/documents/SchemaSetupNotice'
import { SelectionBar } from '@/components/documents/SelectionBar'
import { VideoSoundDialog } from '@/components/documents/VideoSoundQuestion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Card } from '@/components/ui/card'
import { FilterChip } from '@/components/ui/filter-chip'
import { GridTable } from '@/components/ui/grid-table'
import { type ExtractResultsTone, extractResultsTone } from '@/features/documents/extract-results'
import { useDocumentList } from '@/features/documents/hooks/useDocumentList'
import { selectedStructuredMappings } from '@/features/documents/table-processing'
import { AUTO_PIPELINE_ID, AUTO_PIPELINE_LABEL, STATUS_OPTIONS } from '@/features/documents/types'
import { videoRefusalText } from '@/features/documents/video-refusal'
import type { DocumentMapping } from '@/services/api/structuredIngest'
import { cn } from '@/utils/cn'

/** The surface of the result notice: something failed, nothing new was scheduled, or all went as asked. */
const EXTRACT_RESULTS_SURFACE: Record<ExtractResultsTone, string> = {
  warning: 'bg-warning-surface text-warning-foreground',
  info: 'bg-info-surface text-info-foreground',
  success: 'bg-success-surface text-success-foreground',
}

interface DocumentListProps {
  caseId: string
  // Accepted for API compatibility with the documents page; the row-level
  // geo affordance is currently surfaced elsewhere.
  onViewGeoData?: (documentId: string) => void
  onDocumentSelect?: (documentId: string) => void
  selectedDocId?: string | null
  onPathChange?: (path: string | undefined) => void
  refreshKey?: number
  /** Structured-ingest state per document id — tabular files that have a mapping proposal. */
  mappings?: Record<string, DocumentMapping>
  onReviewMapping?: (analysisId: string) => void
  /** Starts preparing the mapping of a table that has none (see DocumentRow). */
  onPrepareMapping?: (documentId: string) => void
  /** Documents whose mapping start is on its way. */
  startingMappings?: ReadonlySet<string>
}

export function DocumentList({
  caseId,
  onDocumentSelect,
  selectedDocId,
  onPathChange,
  refreshKey,
  mappings,
  onReviewMapping,
  onPrepareMapping,
  startingMappings,
}: DocumentListProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const list = useDocumentList({ caseId, onPathChange, refreshKey })
  // Selected files that structured ingest already handles — the run-extraction
  // dialog offers to leave them to their mapping instead of chunking them.
  const structuredSelection = useMemo(
    () => selectedStructuredMappings(list.selectedDocumentMetas, mappings),
    [list.selectedDocumentMetas, mappings]
  )

  return (
    <div className="flex flex-col h-full bg-background max-md:overflow-y-auto">
      {list.schema.status === 'ready' ? (
        <ExtractionStatusCard
          runInProgress={list.runInProgress}
          pendingCount={list.pendingCount}
          failedCount={list.failedCount}
          autoRunning={list.autoRunning}
          onViewRunDetails={() => navigate(`/cases/${caseId}/extraction-runs`)}
          onRunPending={() => list.runExtractionOnPending(['uploaded'])}
          onRetryFailed={() => list.runExtractionOnPending(['failed'])}
        />
      ) : (
        <SchemaSetupBanner
          caseId={caseId}
          status={list.schema.status}
          onRetry={() => void list.schema.refetch()}
        />
      )}
      {/* Toolbar */}
      <div className="px-4 pb-3 pt-3 space-y-3 shrink-0 md:px-6">
        <div className="flex items-center gap-2 [&_[data-slot=command-input-wrapper]]:p-0">
          <div className="relative flex-1">
            <DocumentSearchCombobox
              caseId={caseId}
              value={list.searchQuery}
              onChange={list.setSearchQuery}
            />
          </div>
        </div>

        {/* Status filter chips */}
        <div className="scrollbar-none flex items-center gap-2 flex-wrap max-md:-mx-4 max-md:flex-nowrap max-md:overflow-x-auto max-md:px-4 max-md:[&>*]:shrink-0">
          <FilterChip
            label={t('extraction.all')}
            count={list.statusCounts.all}
            active={list.statusFilters.size === 0}
            onClick={list.clearStatusFilter}
          />
          {STATUS_OPTIONS.map(opt => (
            <FilterChip
              key={opt.value}
              label={opt.label}
              count={list.statusCounts[opt.value] ?? 0}
              active={list.statusFilters.has(opt.value)}
              onClick={() => list.toggleStatusFilter(opt.value)}
            />
          ))}
        </div>
      </div>

      {/* Document area */}
      <div className="flex-1 flex flex-col overflow-hidden px-4 pb-4 max-md:min-h-[28rem] max-md:shrink-0 max-md:overflow-visible md:px-6">
        {/* Breadcrumb — fixed */}
        {/* Phone: the crumbs carry the row's vertical padding, so each one is a 28 px tap target. */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground px-2 py-0.5 shrink-0 md:py-2">
          <FolderClosed className="size-3.5 shrink-0" />
          <div className="scrollbar-none flex items-center gap-1 max-md:min-w-0 max-md:overflow-x-auto max-md:whitespace-nowrap">
            {list.breadcrumbItems.map((item, i) => (
              <span key={i} className="flex items-center gap-1 max-md:shrink-0">
                {i > 0 && <ChevronRight className="size-3 text-muted-foreground/50" />}
                {i === list.breadcrumbItems.length - 1 ? (
                  <span className="text-foreground font-medium max-md:py-1.5">{item}</span>
                ) : (
                  <button
                    className="hover:text-foreground transition-colors cursor-pointer max-md:py-1.5"
                    onClick={() => list.navigateTo(i)}
                  >
                    {item}
                  </button>
                )}
              </span>
            ))}
          </div>
          <span className="ml-auto max-md:shrink-0">
            {list.loading && !list.treeData ? (
              <span className="inline-block size-3 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" />
            ) : (
              `${list.totalItems} items`
            )}
          </span>
        </div>

        {/* Table container */}
        <Card
          className={cn(
            'flex flex-1 flex-col overflow-hidden rounded-xl p-0 min-h-0',
            DOCUMENT_GRID_COLS_CLASS
          )}
        >
          <GridTable columns={DOCUMENT_GRID_COLS}>
            {/* Table header — sticky */}
            <div className="shrink-0">
              <DocumentTableHeader
                selectAllChecked={list.selectAllChecked}
                selectAllIndeterminate={list.selectAllIndeterminate}
                onSelectAll={list.handleSelectAll}
                sorting={{ sort: list.sort, onSort: list.toggleSort }}
              />
            </div>

            {/* Table body — scrollable */}
            <div ref={list.scrollRef} className="flex-1 overflow-auto">
              {list.loading && !list.treeData ? (
                <DocumentTableSkeleton />
              ) : list.displayedItems === 0 ? (
                <DocumentEmptyState />
              ) : (
                <>
                  {list.orderedItems.map((item, index) =>
                    item.type === 'folder' ? (
                      <FolderRow
                        key={item.key}
                        folder={item.folder}
                        isSelected={list.selectedFolderPaths.has(item.key)}
                        index={index}
                        onToggleSelect={list.handleItemToggle}
                        onNavigate={list.navigateInto}
                        onDelete={list.handleDeleteFolder}
                        activeStatus={list.activeStatus}
                      />
                    ) : (
                      <DocumentRow
                        key={item.key}
                        file={item.file}
                        isSelected={list.selectedIds.has(item.key)}
                        index={index}
                        onToggle={list.handleItemToggle}
                        onDocumentSelect={onDocumentSelect}
                        isDetailSelected={item.file.document.id === selectedDocId}
                        onResetExtraction={list.handleResetSingleExtraction}
                        onAnalyseVideo={list.handleAnalyseVideo}
                        videoAvailable={list.videoAvailable}
                        onDelete={list.handleDeleteDocument}
                        mapping={mappings?.[item.file.document.id]}
                        onReviewMapping={onReviewMapping}
                        onPrepareMapping={onPrepareMapping}
                        startingMapping={startingMappings?.has(item.file.document.id)}
                      />
                    )
                  )}
                  {list.loadingMore && (
                    <div className="flex items-center justify-center py-3">
                      <div className="animate-spin rounded-full size-5 border-b-2 border-primary" />
                    </div>
                  )}
                </>
              )}
            </div>
          </GridTable>
        </Card>

        {/* Selection bar — sticky bottom */}
        {list.hasSelection && (
          <div className="shrink-0 pt-3 max-md:sticky max-md:bottom-2 max-md:z-10">
            <SelectionBar
              selection={{
                folderCount: list.selectedFolderPaths.size,
                fileCount: list.selectedIds.size,
                totalItems: list.selectedTotalItems,
                captureCount: list.selectedCaptureCount,
              }}
              extraction={{
                isLoading: list.isRetrying,
                pipelines: list.pipelines,
                selectedPipelineId: list.selectedPipelineId,
                onPipelineSelect: list.handlePipelineSelect,
                onRun: list.handleRetryPipeline,
              }}
              actions={{
                onClear: list.handleClearSelection,
                onReset: () => list.handleResetExtraction(Array.from(list.selectedIds)),
                onDelete: list.handleBulkDelete,
              }}
              schemaSetup={
                list.schema.status !== 'ready' ? (
                  <SchemaSetupPrompt
                    caseId={caseId}
                    status={list.schema.status}
                    onRetry={() => void list.schema.refetch()}
                  />
                ) : undefined
              }
            />
          </div>
        )}

        {/* Whether the sound of the run's videos is processed too (PRD-0115), asked before a run without a
            dialog of its own starts; a re-run of one video says beside it that it replaces what the last
            run found (ADR-0089). */}
        <VideoSoundDialog
          scope={list.videoSoundQuestion.scope}
          onAnswer={list.videoSoundQuestion.answer}
          onCancel={() => list.videoSoundQuestion.answer(null)}
        />

        {/* Extract results — sticky bottom */}
        {list.extractResults && (
          <div className="shrink-0 pt-2">
            <div
              className={cn(
                'space-y-1 px-3 py-2 rounded-lg text-sm',
                EXTRACT_RESULTS_SURFACE[extractResultsTone(list.extractResults)]
              )}
            >
              <span>
                Scheduled: {list.extractResults.scheduled}
                {/* Left alone by the server: documents that are being extracted already. */}
                {list.extractResults.alreadyProcessing > 0 &&
                  `, ${t('cases.documents.extractResults.alreadyProcessing', {
                    count: list.extractResults.alreadyProcessing,
                  })}`}
                {list.extractResults.skipped > 0 &&
                  `, Left to structured ingest: ${list.extractResults.skipped}`}
                {list.extractResults.failed > 0 && `, Failed: ${list.extractResults.failed}`}
              </span>
              {list.extractResults.errors.map((error, index) => (
                <p key={`${error.document_id}-${index}`} className="text-xs">
                  {error.error}
                </p>
              ))}
              {/* Left alone with a reason: a video the deployment cannot analyse (PRD-0074). */}
              {list.extractResults.held.map(held => (
                <p key={held.document_id} className="text-xs">
                  {videoRefusalText(held.code, t) ?? held.code}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={!!list.deleteConfirm}
        onOpenChange={open => {
          if (!open) list.setDeleteConfirm(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete documents</AlertDialogTitle>
            <AlertDialogDescription>{list.deleteConfirm?.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                await list.deleteConfirm?.onConfirm()
                list.setDeleteConfirm(null)
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Extraction config modal */}
      {list.schema.status === 'ready' && (
        <ExtractionConfigModal
          open={list.showExtractionModal}
          onOpenChange={list.setShowExtractionModal}
          caseId={caseId}
          documentCount={list.selectedIds.size}
          pipelineName={
            list.selectedPipelineId === AUTO_PIPELINE_ID
              ? AUTO_PIPELINE_LABEL
              : (list.pipelines.find(p => p.id === list.selectedPipelineId)?.name ??
                list.selectedPipelineId ??
                '')
          }
          selectedDocuments={list.selectedDocumentMetas}
          videoSound={list.selectionVideoSound}
          structuredDocuments={structuredSelection}
          onReviewMapping={onReviewMapping}
          onRun={list.handleRunExtraction}
          isRunning={list.isRetrying}
        />
      )}
    </div>
  )
}
